import React, { useEffect, useRef, useState } from 'react';
import { X, ZoomIn, ZoomOut, Maximize2, ChevronLeft, ChevronRight, Edit2, Check } from 'lucide-react';

/**
 * Fullscreen image viewer with zoom (wheel/buttons) and pan (drag).
 * Supports clickable bounding boxes, drawing new boxes, moving & resizing boxes,
 * and editing class names.
 *
 * Boxes are in IMAGE pixel coordinates: {id, x1, y1, x2, y2, status, label, confidence}.
 */
export default function AerialImageViewer({
  open,
  src,
  imageWidth,
  imageHeight,
  boxes = [],
  selectedBoxId = null,
  onBoxClick,
  drawMode = false,
  onDrawBox,
  onBoxUpdate, // (boxId, {x1, y1, x2, y2, label})
  title,
  actions,
  onClose,
  onPrev,
  onNext,
}) {
  const viewportRef = useRef(null);
  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const dragRef = useRef(null);
  const [drawRect, setDrawRect] = useState(null); // {x0,y0,x1,y1} image px
  const [imgSize, setImgSize] = useState({ w: imageWidth || 0, h: imageHeight || 0 });
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [labelInput, setLabelInput] = useState('');

  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

  const resetView = () => {
    const vp = viewportRef.current;
    if (!vp || !imgSize.w) return;
    const s = Math.min(vp.clientWidth / imgSize.w, vp.clientHeight / imgSize.h) * 0.95;
    setScale(s);
    setTx((vp.clientWidth - imgSize.w * s) / 2);
    setTy((vp.clientHeight - imgSize.h * s) / 2);
  };

  useEffect(() => {
    if (open) {
      setDrawRect(null);
      setEditingLabelId(null);
    }
  }, [open, src]);

  useEffect(() => {
    if (open && imgSize.w) resetView();
  }, [open, imgSize.w, imgSize.h]);

  useEffect(() => {
    const onKey = (e) => {
      if (!open) return;
      if (editingLabelId) return; // don't navigate when typing
      if (e.key === 'Escape') onClose && onClose();
      if (e.key === 'ArrowLeft' && onPrev) onPrev();
      if (e.key === 'ArrowRight' && onNext) onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, onPrev, onNext, editingLabelId]);

  if (!open) return null;

  const onWheel = (e) => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
    const newScale = clamp(scale * factor, 0.05, 20);
    const rect = viewportRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const k = newScale / scale;
    setTx(mx - k * (mx - tx));
    setTy(my - k * (my - ty));
    setScale(newScale);
  };

  const toImageCoords = (clientX, clientY) => {
    const r = innerRef.current.getBoundingClientRect();
    return { x: (clientX - r.left) / scale, y: (clientY - r.top) / scale };
  };

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    if (drawMode) {
      const p = toImageCoords(e.clientX, e.clientY);
      dragRef.current = { mode: 'draw' };
      setDrawRect({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
    } else {
      dragRef.current = { mode: 'pan', clientX: e.clientX, clientY: e.clientY, tx, ty };
    }
  };

  const onHandleMouseDown = (e, b, handle) => {
    e.stopPropagation();
    const p = toImageCoords(e.clientX, e.clientY);
    dragRef.current = {
      mode: 'box_resize',
      boxId: b.id,
      handle,
      startX: p.x,
      startY: p.y,
      orig: { x1: b.x1, y1: b.y1, x2: b.x2, y2: b.y2 },
    };
  };

  const onBoxMouseDown = (e, b) => {
    e.stopPropagation();
    onBoxClick && onBoxClick(b);
    if (!onBoxUpdate) return;
    const p = toImageCoords(e.clientX, e.clientY);
    dragRef.current = {
      mode: 'box_move',
      boxId: b.id,
      startX: p.x,
      startY: p.y,
      orig: { x1: b.x1, y1: b.y1, x2: b.x2, y2: b.y2 },
    };
  };

  const onMouseMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    if (d.mode === 'pan') {
      setTx(d.tx + (e.clientX - d.clientX));
      setTy(d.ty + (e.clientY - d.clientY));
    } else if (d.mode === 'draw' && drawRect) {
      const p = toImageCoords(e.clientX, e.clientY);
      setDrawRect({ ...drawRect, x1: p.x, y1: p.y });
    } else if (d.mode === 'box_move' && onBoxUpdate) {
      const p = toImageCoords(e.clientX, e.clientY);
      const dx = p.x - d.startX;
      const dy = p.y - d.startY;
      const w = d.orig.x2 - d.orig.x1;
      const h = d.orig.y2 - d.orig.y1;
      const nx1 = Math.max(0, d.orig.x1 + dx);
      const ny1 = Math.max(0, d.orig.y1 + dy);
      onBoxUpdate(d.boxId, { x1: nx1, y1: ny1, x2: nx1 + w, y2: ny1 + h });
    } else if (d.mode === 'box_resize' && onBoxUpdate) {
      const p = toImageCoords(e.clientX, e.clientY);
      let { x1, y1, x2, y2 } = d.orig;
      const dx = p.x - d.startX;
      const dy = p.y - d.startY;
      if (d.handle.includes('e')) x2 += dx;
      if (d.handle.includes('w')) x1 += dx;
      if (d.handle.includes('s')) y2 += dy;
      if (d.handle.includes('n')) y1 += dy;
      if (x2 - x1 >= 10 && y2 - y1 >= 10) {
        onBoxUpdate(d.boxId, { x1, y1, x2, y2 });
      }
    }
  };

  const onMouseUp = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (d && d.mode === 'draw' && drawRect && onDrawBox) {
      const x1 = Math.min(drawRect.x0, drawRect.x1);
      const y1 = Math.min(drawRect.y0, drawRect.y1);
      const x2 = Math.max(drawRect.x0, drawRect.x1);
      const y2 = Math.max(drawRect.y0, drawRect.y1);
      if (x2 - x1 > 4 && y2 - y1 > 4) onDrawBox([x1, y1, x2, y2]);
      setDrawRect(null);
    }
  };

  const startEditLabel = (e, b) => {
    e.stopPropagation();
    setEditingLabelId(b.id);
    setLabelInput(b.label || 'machine');
  };

  const commitEditLabel = (boxId) => {
    if (onBoxUpdate && labelInput.trim()) {
      onBoxUpdate(boxId, { label: labelInput.trim() });
    }
    setEditingLabelId(null);
  };

  const boxColor = (b) => (b.status === 'REJECTED' ? '#EF4444' : b.status === 'ACCEPTED' ? '#10B981' : '#F59E0B');

  const handleStyle = (pos) => {
    const size = 8 / scale;
    const offset = -size / 2;
    const base = {
      position: 'absolute',
      width: `${Math.max(6, size)}px`,
      height: `${Math.max(6, size)}px`,
      backgroundColor: '#00E5FF',
      border: '1px solid #000',
      boxSizing: 'border-box',
      zIndex: 5,
    };
    switch (pos) {
      case 'nw': return { ...base, left: `${offset}px`, top: `${offset}px`, cursor: 'nwse-resize' };
      case 'ne': return { ...base, right: `${offset}px`, top: `${offset}px`, cursor: 'nesw-resize' };
      case 'se': return { ...base, right: `${offset}px`, bottom: `${offset}px`, cursor: 'nwse-resize' };
      case 'sw': return { ...base, left: `${offset}px`, bottom: `${offset}px`, cursor: 'nesw-resize' };
      default: return base;
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, backgroundColor: 'rgba(0,0,0,0.92)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', color: '#fff', borderBottom: '1px solid #333', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {actions}
          <button onClick={onPrev} disabled={!onPrev} title="Previous image" style={{ ...iconBtn, opacity: onPrev ? 1 : 0.35, cursor: onPrev ? 'pointer' : 'not-allowed' }}><ChevronLeft size={18} /></button>
          <button onClick={onNext} disabled={!onNext} title="Next image" style={{ ...iconBtn, opacity: onNext ? 1 : 0.35, cursor: onNext ? 'pointer' : 'not-allowed' }}><ChevronRight size={18} /></button>
          <button onClick={() => setScale((s) => clamp(s * 1.25, 0.05, 20))} title="Zoom in" style={iconBtn}><ZoomIn size={16} /></button>
          <button onClick={() => setScale((s) => clamp(s / 1.25, 0.05, 20))} title="Zoom out" style={iconBtn}><ZoomOut size={16} /></button>
          <button onClick={resetView} title="Fit" style={iconBtn}><Maximize2 size={16} /></button>
          <button onClick={onClose} title="Close" style={iconBtn}><X size={16} /></button>
        </div>
      </div>

      {/* Viewport */}
      <div
        ref={viewportRef}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={() => { dragRef.current = null; setDrawRect(null); }}
        style={{ flex: 1, position: 'relative', overflow: 'hidden', cursor: drawMode ? 'crosshair' : 'grab' }}
      >
        <div
          ref={innerRef}
          style={{ position: 'absolute', left: 0, top: 0, transform: `translate(${tx}px, ${ty}px) scale(${scale})`, transformOrigin: '0 0', willChange: 'transform' }}
        >
          <img
            src={src}
            alt="slice"
            draggable={false}
            onLoad={(e) => setImgSize({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
            style={{ display: 'block', userSelect: 'none', pointerEvents: 'none' }}
          />
          {boxes.map((b) => {
            const isSelected = selectedBoxId === b.id;
            return (
              <div
                key={b.id}
                onMouseDown={(e) => onBoxMouseDown(e, b)}
                style={{
                  position: 'absolute',
                  left: b.x1, top: b.y1,
                  width: Math.max(1, b.x2 - b.x1), height: Math.max(1, b.y2 - b.y1),
                  border: `3px solid ${isSelected ? '#00E5FF' : boxColor(b)}`,
                  boxSizing: 'border-box',
                  cursor: isSelected ? 'move' : (onBoxClick ? 'pointer' : 'default'),
                  boxShadow: isSelected ? '0 0 0 2px #00E5FF inset' : 'none',
                }}
              >
                {/* Resize handles when selected */}
                {isSelected && onBoxUpdate && (
                  <>
                    <div style={handleStyle('nw')} onMouseDown={(e) => onHandleMouseDown(e, b, 'nw')} />
                    <div style={handleStyle('ne')} onMouseDown={(e) => onHandleMouseDown(e, b, 'ne')} />
                    <div style={handleStyle('se')} onMouseDown={(e) => onHandleMouseDown(e, b, 'se')} />
                    <div style={handleStyle('sw')} onMouseDown={(e) => onHandleMouseDown(e, b, 'sw')} />
                  </>
                )}

                {/* Box label header */}
                <div
                  onMouseDown={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute', top: -20, left: 0,
                    fontSize: '11px', color: '#fff', backgroundColor: boxColor(b),
                    padding: '1px 6px', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px',
                    borderRadius: '2px', zIndex: 10,
                  }}
                >
                  {editingLabelId === b.id ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <input
                        type="text"
                        autoFocus
                        value={labelInput}
                        onChange={(e) => setLabelInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitEditLabel(b.id);
                          if (e.key === 'Escape') setEditingLabelId(null);
                        }}
                        style={{
                          backgroundColor: '#000', color: '#fff', border: '1px solid #fff',
                          fontSize: '11px', padding: '1px 4px', width: '80px', outline: 'none',
                        }}
                      />
                      <Check size={12} style={{ cursor: 'pointer' }} onClick={() => commitEditLabel(b.id)} />
                    </div>
                  ) : (
                    <>
                      <span>{b.label || 'machine'}</span>
                      {b.confidence != null && <span style={{ opacity: 0.8 }}>({Number(b.confidence).toFixed(2)})</span>}
                      {b.status === 'REJECTED' && <span>✕</span>}
                      {onBoxUpdate && (
                        <Edit2
                          size={11}
                          style={{ cursor: 'pointer', marginLeft: '2px' }}
                          title="Edit label"
                          onClick={(e) => startEditLabel(e, b)}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {drawRect && (
            <div style={{
              position: 'absolute',
              left: Math.min(drawRect.x0, drawRect.x1), top: Math.min(drawRect.y0, drawRect.y1),
              width: Math.abs(drawRect.x1 - drawRect.x0), height: Math.abs(drawRect.y1 - drawRect.y0),
              border: '3px dashed #10B981', boxSizing: 'border-box',
            }} />
          )}
        </div>

        {/* Floating navigation arrows */}
        <button
          onClick={(e) => { e.stopPropagation(); onPrev && onPrev(); }}
          title="Previous image"
          style={{ ...navBtn, left: '12px', opacity: onPrev ? 0.9 : 0.25, cursor: onPrev ? 'pointer' : 'not-allowed' }}
        >
          <ChevronLeft size={30} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onNext && onNext(); }}
          title="Next image"
          style={{ ...navBtn, right: '12px', opacity: onNext ? 0.9 : 0.25, cursor: onNext ? 'pointer' : 'not-allowed' }}
        >
          <ChevronRight size={30} />
        </button>
      </div>
    </div>
  );
}

const iconBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: '32px', height: '32px', borderRadius: '6px', border: '1px solid #444',
  background: '#1b1b1b', color: '#fff', cursor: 'pointer',
};

const navBtn = {
  position: 'absolute', top: '50%', transform: 'translateY(-50%)',
  width: '56px', height: '56px', borderRadius: '50%', border: 'none',
  background: 'rgba(0,0,0,0.55)', color: '#fff', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 10,
};
